import { julian, sidereal, coord } from 'astronomia';

// Ephemeris utilities for astrology calculations
// Updated to work with actual ephemeris data from public/astrodata/data

// Cache for loaded ephemeris data
const ephemerisCache = new Map();

/**
 * Generate simulated planetary positions for dates without ephemeris data
 * @param {Date} birthDate - The birth date
 * @returns {Object} - Simulated planetary positions
 */
function generateSimulatedPlanetaryPositions(birthDate) {
  // Base positions for planets (simplified)
  const basePositions = {
    'Sun': { baseLongitude: 0, speed: 1 },
    'Moon': { baseLongitude: 0, speed: 13.2 },
    'Mercury': { baseLongitude: 0, speed: 1.4 },
    'Venus': { baseLongitude: 0, speed: 1.2 },
    'Mars': { baseLongitude: 0, speed: 0.5 },
    'Jupiter': { baseLongitude: 0, speed: 0.08 },
    'Saturn': { baseLongitude: 0, speed: 0.03 },
    'Uranus': { baseLongitude: 0, speed: 0.01 },
    'Neptune': { baseLongitude: 0, speed: 0.006 },
    'Pluto': { baseLongitude: 0, speed: 0.004 }
  };

  // Calculate days since a reference date (e.g., 2000-01-01)
  const referenceDate = new Date('2000-01-01');
  const daysSinceReference = (birthDate - referenceDate) / (1000 * 60 * 60 * 24);

  const planets = {};
  
  for (const [planetName, planetData] of Object.entries(basePositions)) {
    // Calculate current longitude based on speed and time
    const longitude = (planetData.baseLongitude + (planetData.speed * daysSinceReference)) % 360;
    
    // Add some realistic variation
    const variation = Math.sin(daysSinceReference * 0.1) * 5;
    const finalLongitude = (longitude + variation + 360) % 360;
    
    planets[planetName] = {
      longitude: finalLongitude,
      latitude: (Math.random() - 0.5) * 10, // Random latitude within ±5 degrees
      distance: 1 + Math.random() * 0.5 // Random distance variation
    };
  }

  return {
    date: birthDate.toISOString().split('T')[0],
    julian_day: 2451545 + daysSinceReference, // Approximate Julian Day
    planets: planets
  };
}

/**
 * Load ephemeris data for a specific year
 * @param {number} year - The year to load data for
 * @returns {Promise<Array>} - Array of daily ephemeris data
 */
export async function loadEphemerisData(year) {
  // Check cache first
  if (ephemerisCache.has(year)) {
    return ephemerisCache.get(year);
  }

  try {
    const response = await fetch(`/astrodata/data/ephemeris-${year}.json`);
    if (!response.ok) {
      throw new Error(`Failed to load ephemeris data for year ${year}`);
    }
    
    const data = await response.json();
    
    // Check if data is empty
    if (!data || data.length === 0) {
      console.warn(`Ephemeris data for year ${year} is empty, will use simulated data`);
      return null;
    }
    
    // Cache the data
    ephemerisCache.set(year, data);
    
    return data;
  } catch (error) {
    console.error(`Error loading ephemeris data for year ${year}:`, error);
    return null;
  }
}

/**
 * Find the closest ephemeris entry for a given date
 * @param {Array} ephemerisData - Array of ephemeris data
 * @param {Date} targetDate - The target date
 * @returns {Object|null} - The closest ephemeris entry
 */
export function findClosestEphemerisEntry(ephemerisData, targetDate) {
  if (!ephemerisData || ephemerisData.length === 0) {
    return null;
  }

  const targetTime = targetDate.getTime();
  let closestEntry = ephemerisData[0];
  let minDifference = Math.abs(new Date(closestEntry.date).getTime() - targetTime);

  for (const entry of ephemerisData) {
    const entryTime = new Date(entry.date).getTime();
    const difference = Math.abs(entryTime - targetTime);
    
    if (difference < minDifference) {
      minDifference = difference;
      closestEntry = entry;
    }
  }

  return closestEntry;
}

/**
 * Get planetary positions for a specific date
 * @param {Date} birthDate - The birth date
 * @returns {Promise<Object>} - Planetary positions
 */
export async function getPlanetaryPositions(birthDate) {
  const year = birthDate.getFullYear();
  
  // Load ephemeris data for the birth year
  const ephemerisData = await loadEphemerisData(year);
  
  if (!ephemerisData) {
    console.warn(`No ephemeris data available for year ${year}, using simulated data`);
    return generateSimulatedPlanetaryPositions(birthDate);
  }

  // Find the closest entry to the birth date
  const closestEntry = findClosestEphemerisEntry(ephemerisData, birthDate);
  
  if (!closestEntry) {
    console.warn('No ephemeris entry found for the given date, using simulated data');
    return generateSimulatedPlanetaryPositions(birthDate);
  }

  return {
    date: closestEntry.date,
    julianDay: closestEntry.julian_day,
    planets: closestEntry.planets
  };
}

/**
 * Calculate house cusps using the Whole Sign house system
 * @param {number} ascendantLongitude - The ecliptic longitude of the Ascendant (degrees)
 * @returns {Array} - Array of house cusps (1-12)
 */
export function calculateHouseCuspsWholeSign(ascendantLongitude) {
  // Find the sign number of the Ascendant (0 = Aries, 1 = Taurus, ..., 10 = Aquarius, 11 = Pisces)
  const ascSign = Math.floor(((ascendantLongitude % 360) + 360) % 360 / 30);
  const houses = [];
  for (let i = 0; i < 12; i++) {
    // Each house cusp is at 0° of the sign, wrapping around
    const signNum = (ascSign + i) % 12;
    const angle = signNum * 30;
    houses.push({
      house: i + 1,
      angle,
      sign: signNum + 1, // 1-based for Aries=1, Taurus=2, etc.
      degree: 0
    });
  }
  return houses;
}

/**
 * Get zodiac sign from longitude
 * @param {number} longitude - Planetary longitude in degrees
 * @returns {Object} - Sign information
 */
export function getZodiacSign(longitude) {
  const signs = [
    { name: 'Aries', element: 'Fire', start: 0, end: 30 },
    { name: 'Taurus', element: 'Earth', start: 30, end: 60 },
    { name: 'Gemini', element: 'Air', start: 60, end: 90 },
    { name: 'Cancer', element: 'Water', start: 90, end: 120 },
    { name: 'Leo', element: 'Fire', start: 120, end: 150 },
    { name: 'Virgo', element: 'Earth', start: 150, end: 180 },
    { name: 'Libra', element: 'Air', start: 180, end: 210 },
    { name: 'Scorpio', element: 'Water', start: 210, end: 240 },
    { name: 'Sagittarius', element: 'Fire', start: 240, end: 270 },
    { name: 'Capricorn', element: 'Earth', start: 270, end: 300 },
    { name: 'Aquarius', element: 'Air', start: 300, end: 330 },
    { name: 'Pisces', element: 'Water', start: 330, end: 360 }
  ];

  const normalizedLongitude = longitude % 360;
  
  for (const sign of signs) {
    if (normalizedLongitude >= sign.start && normalizedLongitude < sign.end) {
      return {
        ...sign,
        degree: normalizedLongitude - sign.start
      };
    }
  }
  
  // Handle edge case (360 degrees)
  return {
    ...signs[0],
    degree: 0
  };
}

/**
 * Format planetary position for display
 * @param {Object} planet - Planet data with longitude
 * @returns {Object} - Formatted planet information
 */
export function formatPlanetPosition(planet) {
  const sign = getZodiacSign(planet.longitude);
  return {
    longitude: planet.longitude,
    latitude: planet.latitude,
    distance: planet.distance,
    sign: sign.name,
    signElement: sign.element,
    degree: Math.floor(sign.degree),
    minute: Math.floor((sign.degree % 1) * 60)
  };
}

/**
 * Get planet image path
 * @param {string} planetName - Name of the planet
 * @returns {string} - Path to planet image
 */
export function getPlanetImage(planetName) {
  const planetImages = {
    'Sun': '/img/planets/sun.png',
    'Moon': '/img/planets/moon.png',
    'Mercury': '/img/planets/mercury.png',
    'Venus': '/img/planets/venus.png',
    'Mars': '/img/planets/mars.png',
    'Jupiter': '/img/planets/jupiter.png',
    'Saturn': '/img/planets/saturn.png',
    'Uranus': '/img/planets/uranus.png',
    'Neptune': '/img/planets/neptune.png',
    'Pluto': '/img/planets/pluto.png'
  };
  
  return planetImages[planetName] || '/img/planets/sun.png';
}

/**
 * Get house system information
 * @param {string} houseSystem - House system identifier
 * @returns {Object} - House system information
 */
export function getHouseSystemInfo(houseSystem) {
  const houseSystems = {
    'whole-sign': {
      name: 'Whole Sign',
      description: 'Ancient system where each house spans exactly 30° (one sign). The Ascendant sign becomes the 1st house.',
      era: 'Ancient',
      complexity: 'Simple',
      popularity: 'High in traditional astrology'
    },
    'placidus': {
      name: 'Placidus',
      description: 'Modern system based on time and space divisions. Most popular in Western astrology.',
      era: 'Modern',
      complexity: 'Medium',
      popularity: 'Most popular in modern astrology'
    },
    'koch': {
      name: 'Koch',
      description: 'Modern system that divides the time it takes for a degree of the ecliptic to rise.',
      era: 'Modern',
      complexity: 'Medium',
      popularity: 'Popular in German-speaking countries'
    },
    'equal-house': {
      name: 'Equal House',
      description: 'Ancient system where all houses are exactly 30° wide, starting from the Ascendant.',
      era: 'Ancient',
      complexity: 'Simple',
      popularity: 'Popular in Vedic astrology'
    },
    'campanus': {
      name: 'Campanus',
      description: 'Medieval system that divides the prime vertical into 12 equal parts.',
      era: 'Medieval',
      complexity: 'Medium',
      popularity: 'Used in traditional astrology'
    },
    'regiomontanus': {
      name: 'Regiomontanus',
      description: 'Medieval system that divides the celestial equator into 12 equal parts.',
      era: 'Medieval',
      complexity: 'Medium',
      popularity: 'Used in traditional astrology'
    },
    'topocentric': {
      name: 'Topocentric',
      description: 'Modern system that considers the observer\'s specific location on Earth.',
      era: 'Modern',
      complexity: 'High',
      popularity: 'Used by some modern astrologers'
    }
  };

  return houseSystems[houseSystem] || houseSystems['whole-sign'];
}

/**
 * Get detailed house system comparison information
 * @returns {Array} - Array of house system objects with detailed information
 */
export function getHouseSystemComparison() {
  return [
    {
      value: 'whole-sign',
      name: 'Whole Sign',
      description: 'Each house spans exactly 30° (one sign). The Ascendant sign becomes the 1st house.',
      era: 'Ancient',
      complexity: 'Simple',
      popularity: 'High in traditional astrology',
      pros: ['Simple to understand', 'Traditional approach', 'Clear sign boundaries'],
      cons: ['May not reflect actual house sizes', 'Less precise for timing'],
      bestFor: 'Traditional astrology, beginners, Hellenistic techniques'
    },
    {
      value: 'placidus',
      name: 'Placidus',
      description: 'Based on time and space divisions. Most popular in Western astrology.',
      era: 'Modern',
      complexity: 'Medium',
      popularity: 'Most popular in modern astrology',
      pros: ['Most widely used', 'Good for timing', 'Reflects actual house sizes'],
      cons: ['Complex calculation', 'May not work at extreme latitudes'],
      bestFor: 'Modern astrology, timing techniques, general use'
    },
    {
      value: 'koch',
      name: 'Koch',
      description: 'Modern system that divides the ecliptic based on time.',
      era: 'Modern',
      complexity: 'Medium',
      popularity: 'Popular in German-speaking countries',
      pros: ['Good for timing', 'Works well at all latitudes'],
      cons: ['Less widely used than Placidus'],
      bestFor: 'Timing techniques, German astrology'
    },
    {
      value: 'equal-house',
      name: 'Equal House',
      description: 'Each house spans exactly 30°, starting from the Ascendant degree.',
      era: 'Ancient',
      complexity: 'Simple',
      popularity: 'Moderate',
      pros: ['Simple calculation', 'Works at all latitudes', 'Consistent house sizes'],
      cons: ['May not reflect actual house divisions'],
      bestFor: 'Simple calculations, extreme latitudes'
    },
    {
      value: 'campanus',
      name: 'Campanus',
      description: 'Medieval system that divides the prime vertical.',
      era: 'Medieval',
      complexity: 'Medium',
      popularity: 'Historical interest',
      pros: ['Historical significance', 'Unique perspective'],
      cons: ['Less commonly used today'],
      bestFor: 'Historical astrology, research'
    },
    {
      value: 'regiomontanus',
      name: 'Regiomontanus',
      description: 'Medieval system that divides the celestial equator.',
      era: 'Medieval',
      complexity: 'Medium',
      popularity: 'Historical interest',
      pros: ['Historical significance', 'Good mathematical foundation'],
      cons: ['Less commonly used today'],
      bestFor: 'Historical astrology, research'
    },
    {
      value: 'topocentric',
      name: 'Topocentric',
      description: 'Modern system designed to work at all latitudes.',
      era: 'Modern',
      complexity: 'High',
      popularity: 'Specialized use',
      pros: ['Works at all latitudes', 'Precise calculations'],
      cons: ['Complex calculation', 'Less widely used'],
      bestFor: 'Extreme latitudes, precision work'
    }
  ];
}

export function calculateAscendant(date, latitude, longitude) {
  // Ensure we have a proper Date object
  let dateObj = date;
  if (date && typeof date.toJSDate === 'function') {
    // It's a Luxon DateTime object
    dateObj = date.toJSDate();
  } else if (!(date instanceof Date)) {
    // Try to convert to Date
    dateObj = new Date(date);
  }
  
  // Extract date parts
  const year = dateObj.getUTCFullYear();
  const month = dateObj.getUTCMonth() + 1;
  const day = dateObj.getUTCDate();
  const hour = dateObj.getUTCHours() + dateObj.getUTCMinutes() / 60 + dateObj.getUTCSeconds() / 3600;
  
  // Debug logging
  console.log('🔍 ASTRONOMIA LIBRARY - Ascendant calculation debug:');
  console.log('Date:', dateObj.toISOString());
  console.log('Year:', year, 'Month:', month, 'Day:', day, 'Hour:', hour);
  console.log('Latitude:', latitude, 'Longitude:', longitude);
  
  // Use astronomia library for accurate calculations
  const jd = julian.DateToJD(year, month, day, hour);
  const gmstDeg = sidereal.apparent(jd);
  const lstDeg = gmstDeg + longitude;
  
  console.log('🔍 ASTRONOMIA - Julian Day:', jd);
  console.log('🔍 ASTRONOMIA - GMST:', gmstDeg.toFixed(2));
  console.log('🔍 ASTRONOMIA - LST:', lstDeg.toFixed(2));
  
  // Calculate ascendant using proper astronomical formula
  const lstRad = lstDeg * Math.PI / 180;
  const latRad = latitude * Math.PI / 180;
  
  // Get obliquity of ecliptic
  const T = (jd - 2451545.0) / 36525.0;
  const obliquity = 84381.406 - 46.836769 * T - 0.0001831 * T * T + 0.00200340 * T * T * T;
  const oblRad = (obliquity / 3600) * Math.PI / 180;
  
  const sinLST = Math.sin(lstRad);
  const cosLST = Math.cos(lstRad);
  const sinLat = Math.sin(latRad);
  const cosLat = Math.cos(latRad);
  const sinObl = Math.sin(oblRad);
  const cosObl = Math.cos(oblRad);
  
  // Calculate ascendant using proper formula
  const tanAsc = (sinLST * cosObl + cosLST * sinObl * sinLat) / cosLST;
  let ascRad = Math.atan(tanAsc);
  
  // Apply quadrant adjustment
  if (lstDeg >= 180) {
    ascRad += Math.PI;
  }
  
  let ascDeg = ascRad * 180 / Math.PI;
  if (ascDeg < 0) ascDeg += 360;
  
  console.log('🔍 ASTRONOMIA - Calculated Ascendant:', ascDeg.toFixed(2), '°');
  
  // Check what sign this gives
  const signNum = Math.floor(ascDeg / 30);
  const signNames = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
  console.log('🔍 ASTRONOMIA - CALCULATED ASCENDANT SIGN:', signNames[signNum], `(${ascDeg.toFixed(2)}°)`);
  console.log('🔍 ASTRONOMIA - EXPECTED: Aquarius (300-330°)');
  console.log('🔍 ASTRONOMIA - IS AQUARIUS?', signNum === 10 ? 'YES!' : 'NO - should be 10 for Aquarius');
  
  return ascDeg;
}

/**
 * Generate complete birth chart data
 * @param {Date} birthDate - Birth date
 * @param {number} latitude - Birth latitude
 * @param {number} longitude - Birth longitude
 * @param {boolean} showModernPlanets - Whether to include Uranus and Neptune
 * @returns {Promise<Object>} - Complete birth chart data
 */
export async function generateBirthChart(birthDate, latitude, longitude, showModernPlanets = false) {
  try {
    // Get planetary positions
    const planetaryData = await getPlanetaryPositions(birthDate);
    if (!planetaryData) {
      throw new Error('Failed to load planetary positions');
    }

    // Calculate the true Ascendant longitude
    const ascendantLongitude = calculateAscendant(birthDate, latitude, longitude);

    // Now use the true Whole Sign house system
    const houseCusps = calculateHouseCuspsWholeSign(ascendantLongitude);

    // Format planetary positions and filter modern planets if needed
    const formattedPlanets = {};
    for (const [planetName, planetData] of Object.entries(planetaryData.planets)) {
      // Filter out modern planets unless showModernPlanets is true
      if (!showModernPlanets && ['Uranus', 'Neptune'].includes(planetName)) {
        continue;
      }
      
      formattedPlanets[planetName] = {
        ...formatPlanetPosition(planetData),
        image: getPlanetImage(planetName)
      };
    }

    const chartData = {
      birthDate: birthDate.toISOString(),
      latitude,
      longitude,
      julianDay: planetaryData.julianDay,
      planets: formattedPlanets,
      houses: houseCusps,
      showModernPlanets,
      ascendantLongitude
    };
    
    // BONUS DEBUG: Final Chart Data
    console.log('🔍 FINAL CHART DATA:', {
      ascendantLongitude: chartData.ascendantLongitude,
      houseCusps: chartData.houses.map(h => h.angle),
      planets: Object.keys(chartData.planets)
    });
    
    return chartData;
  } catch (error) {
    console.error('Error generating birth chart:', error);
    throw error;
  }
} 